import { useEffect, useRef } from "react";
import { useSearchParams } from "react-router";

import { useDocumentViewer } from "../contexts/useDocumentViewer";

/**
 * Mount-once effect that auto-opens the `DocumentViewerPanel` when the
 * chat URL carries `?open_document=<id>` (optionally with `&page=<n>`),
 * then strips the two params from the URL.
 *
 * ## Why this exists
 *
 * The Polly xlsx export writes hyperlinks of the form
 * `/organizations/chat/{chatId}?open_document={docId}&page={page}`. When
 * a broker clicks one of those cells in Excel, the browser lands here;
 * this hook is the receiver that turns the URL state back into a viewer
 * open. Clearing the params serves two goals: the effect can't re-fire
 * when unrelated query params change, and a URL shared after landing
 * doesn't leak the viewer-open intent to a new reader.
 *
 * ## Guarantees
 *
 * - Runs at most once per mount (`hasRunRef` guards Strict Mode's
 *   double-invoke; `useEffect(..., [])` guards subsequent re-renders).
 * - Malformed params (non-numeric, zero, negative) are treated as
 *   "no-op" -- silent failure is acceptable here; a broken Excel link
 *   should not produce a loud error in the chat surface.
 * - `page` is optional. A missing or unparseable `page` falls through
 *   to `openDocumentById(docId, undefined)`, which lets the viewer use
 *   whatever default it has (typically page 1).
 * - On success, `setSearchParams({ ... }, { replace: true })` rewrites
 *   the URL in-place, so there's no back-button "reopen viewer" step
 *   that a user didn't ask for.
 *
 * ## Where this mounts
 *
 * Inside `ChatLayout`, which already runs inside `DocumentViewerProvider`.
 * The hook requires the viewer context, so mounting it any higher than
 * `ChatLayout` would throw.
 */
export function useOpenDocumentFromUrlEffect(): void {
    const [params, setParams] = useSearchParams();
    const { openDocumentById } = useDocumentViewer();
    const hasRunRef = useRef(false);

    useEffect(() => {
        if (hasRunRef.current) return;

        const docRaw = params.get("open_document");
        if (!docRaw) return;

        const docId = parseInt(docRaw, 10);
        if (!Number.isFinite(docId) || docId <= 0) return;

        const pageRaw = params.get("page");
        const pageParsed = pageRaw ? parseInt(pageRaw, 10) : NaN;
        const page =
            Number.isFinite(pageParsed) && pageParsed > 0
                ? pageParsed
                : undefined;

        hasRunRef.current = true;
        void openDocumentById(docId, page);

        setParams(
            (prev) => {
                const next = new URLSearchParams(prev);
                next.delete("open_document");
                next.delete("page");
                return next;
            },
            { replace: true }
        );
        // Intentional empty dep list: this is a mount-time consumer.
        // Reading the params via the closure is fine because we only
        // want the *initial* URL state, not live updates.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
}
